import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Loader2, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';
import { useCatalog } from '../data/catalog';
import { useLang } from '../i18n';
import { useSEO } from '../seo';
import { PageHeader } from '../components/PageHeader';
import { Reveal } from '../components/Reveal';
import { ConsentCheckboxes } from '../components/ConsentCheckboxes';
import { useCart } from '../utils/cart';
import { useCuratedCopy } from '../data/curatedArchitecture';
import { EcoAssistContextButton } from '../components/EcoAssistContextButton';
import { DestinationWeather, WEATHER_KEY_OF_DESTINATION } from '../components/DestinationWeather';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = 'eco-input';

const WIZ_DESTINATIONS = ['São Tomé', 'Príncipe', 'Cabo Verde', 'São Tomé + Príncipe'];
const ISLAND_OF = { 'São Tomé': 'São Tomé', 'Príncipe': 'Príncipe', 'Cabo Verde': 'Santiago', 'São Tomé + Príncipe': 'São Tomé' };
const INTERESTS = [
  { id: 'Natureza', key: 'cat.nature' }, { id: 'Praia', key: 'int.beach' }, { id: 'Cultura', key: 'cat.culture' },
  { id: 'Vida Selvagem', key: 'wiz.int.wildlife' }, { id: 'Safari', key: 'wiz.int.safari' }, { id: 'Gastronomia', key: 'cat.gastro' },
  { id: 'Caminhada', key: 'int.hike' }, { id: 'Mar', key: 'cat.sea' }, { id: 'Comunidade', key: 'wiz.int.community' },
  { id: 'Bem-estar', key: 'wiz.int.wellness' }, { id: 'Aventura', key: 'int.adventure' }, { id: 'Relaxamento', key: 'int.relax' },
];
const NEEDS = [
  { id: 'Complete Trip', key: 'wiz.need.trip' }, { id: 'Flights', key: 'nav.flights' }, { id: 'Accommodation', key: 'wiz.need.stay' },
  { id: 'Experiences', key: 'wiz.need.exp' }, { id: 'Transfers', key: 'wiz.need.tr' }, { id: 'Rent-a-Car', key: 'journey.rentcar' }, { id: 'Local Guide', key: 'wiz.need.guide' },
];
const ACCOM_OPTS = [
  { id: 'Hotel', key: 'journey.stay.hotel' }, { id: 'Eco-lodge', key: 'wiz.acc.eco' }, { id: 'Guesthouse', key: 'journey.stay.guesthouse' },
  { id: 'Other', key: 'journey.stay.other' }, { id: 'No accommodation assistance', key: 'journey.stay.none' },
];
const TRANSPORT_OPTS = [
  { id: 'Airport transfer', key: 'journey.transport.airport' }, { id: 'Private transfer', key: 'journey.transport.private' },
  { id: 'Rent-a-Car', key: 'journey.rentcar' }, { id: 'No transport assistance', key: 'journey.none' },
];
const TRAV_OPTS = ['hero.trav1', 'hero.trav2', 'hero.trav34', 'hero.trav5'];
const JOURNEY_STYLES = ['authentic', 'comfort', 'premium', 'signature'];
const labelOf = (list, id, t) => t(list.find((x) => x.id === id)?.key || id);

const STEPS = ['destination', 'dates', 'travellers', 'style', 'interests', 'needs', 'accommodation', 'notes', 'review'];

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-colors duration-200 ${
      active ? 'border-eco-green bg-eco-green text-white' : 'border-eco-ink/15 bg-white text-eco-ink/70 hover:border-eco-gold'
    }`}
  >
    {children}
  </button>
);

export default function PlanMyTripPage() {
  const { t, lang } = useLang();
  const curated = useCuratedCopy(lang);
  const { experiences, packageBySlug } = useCatalog();
  const { items: cartItems, clear: clearCart, getRequestId, rotateRequestId } = useCart();
  const [params] = useSearchParams();
  const isPartner = params.get('partner');
  const preDest = params.get('destination');
  const prePackage = packageBySlug(params.get('package') || '');
  const preProductId = params.get('product_id') || '';
  const preNotes = params.get('notes') || '';
  const cartNote = cartItems.length
    ? `${t('cart.title')}: ${cartItems.map((i) => `${i.name} [${t(`cart.type.${i.type}`)}]`).join(' · ')}`
    : '';

  useSEO(`${t('journey.build')} | Eco Collection`, t('journey.ecosystem.s'));

  const [step, setStep] = useState(0);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [consents, setConsents] = useState({ terms: false, cancellation: false, marketing: false });
  const [data, setData] = useState({
    destinations: preDest ? [preDest] : [],
    dates: '', departure_date: '', return_date: '', flexible_dates: false, travelers: 'hero.trav2', interests: [], needs: [], accommodation: 'No accommodation assistance', transport_preferences: [],
    departure_country: '', departure_airport: '', cabin_preference: '', inter_island_flight_assistance: false, preferred_language: lang, country: '',
    travelStyle: '', experiences: cartItems.filter((i) => i.type === 'experience').map((i) => i.slug), notes: preNotes || (prePackage ? `${t('wiz.pkg.note')}${prePackage.name}` : isPartner ? `${t('wiz.partner.note')}${isPartner}` : cartNote),
    name: '', email: '', whatsapp: '',
  });

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggle = (k, v) => setData((d) => ({ ...d, [k]: d[k].includes(v) ? d[k].filter((x) => x !== v) : [...d[k], v] }));

  const islandPool = data.destinations.map((d) => ISLAND_OF[d]).filter(Boolean);
  const expPool = experiences.filter((e) => islandPool.length === 0 || islandPool.includes(e.island));

  const canNext = () => (STEPS[step] === 'destination' ? data.destinations.length > 0 : true);

  const submit = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API}/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: params.get('partner') === '1' ? 'partner' : 'trip',
          name: data.name, email: data.email, whatsapp: data.whatsapp,
          destination: data.destinations.join(' + '),
          dates: data.dates, travelers: t(data.travelers),
          traveller_profile: data.travelStyle ? `Journey style: ${data.travelStyle}` : '',
          interests: data.interests, accommodation: data.accommodation,
          accommodation_needed: data.accommodation !== 'No accommodation assistance',
          accommodation_preferences: data.accommodation !== 'No accommodation assistance' ? [data.accommodation] : [],
          transfers_needed: data.needs.join(', '), transport_preferences: data.transport_preferences,
          flight_assistance: data.needs.includes('Flights'), inter_island_flight_assistance: data.inter_island_flight_assistance,
          departure_country: data.departure_country, departure_airport: data.departure_airport,
          departure_date: data.departure_date, return_date: data.return_date, flexible_dates: data.flexible_dates,
          cabin_preference: data.cabin_preference, preferred_language: data.preferred_language, country: data.country,
          message: data.notes,
          package_name: prePackage?.name || '',
          product_id: preProductId,
          experience: data.experiences.map((slug) => experiences.find((item) => item.slug === slug)?.name || slug).join(', '),
          selected_products: data.experiences.map((slug) => experiences.find((item) => item.slug === slug)?.id).filter(Boolean),
          ...(cartItems.length && !isPartner ? {
            cart_items: cartItems.map(({ type, slug, name, destination, qty, dates, passengers, notes }) => ({ type, slug, name, destination, qty, dates, passengers, notes })),
            client_request_id: getRequestId(),
          } : {}),
          consent_terms: consents.terms,
          consent_cancellation: consents.cancellation,
          consent_marketing: consents.marketing,
        }),
      });
      if (!res.ok) throw new Error('failed');
      if (cartItems.length && !isPartner) { rotateRequestId(); clearCart(); }
      setDone(await res.json());
    } catch {
      toast.error(t('join.error'));
    } finally {
      setSending(false);
    }
  };

  const reviewRows = [
    [t('wiz.destination'), data.destinations.join(' + ')],
    [t('wiz.dates'), [data.departure_date, data.return_date].filter(Boolean).join(' — ') || data.dates],
    [t('wiz.travellers'), t(data.travelers)],
    [curated.stylesTitle, data.travelStyle ? curated.styles[data.travelStyle] : ''],
    [t('wiz.interests'), data.interests.map((i) => labelOf(INTERESTS, i, t)).join(', ')],
    [t('wiz.needs'), data.needs.map((n) => labelOf(NEEDS, n, t)).join(', ')],
    [t('wiz.accommodation'), labelOf(ACCOM_OPTS, data.accommodation, t)],
    [t('journey.flight'), data.needs.includes('Flights') ? [data.departure_airport, data.departure_country].filter(Boolean).join(', ') || t('journey.flight') : ''],
    [t('journey.transport'), data.transport_preferences.map((item) => labelOf(TRANSPORT_OPTS, item, t)).join(', ')],
    [t('journey.language'), data.preferred_language.toUpperCase()],
    [t('wiz.experiences'), data.experiences.map((slug) => experiences.find((item) => item.slug === slug)?.name || slug).join(', ')],
    [t('wiz.notes'), data.notes],
  ].filter(([, v]) => v);

  return (
    <div data-testid="plan-my-trip-page">
      <PageHeader
        kicker={params.get('partner') === '1' ? t('cta.become') : t('page.plan.k')}
        title={params.get('partner') === '1' ? t('cta.become') : t('page.plan.t')}
        sub={params.get('partner') === '1' ? t('wiz.partner.s') : t('page.plan.s')}
      />
      {params.get('mode') === 'tailor-made' && <div className="bg-white py-5"><div className="eco-container flex justify-center"><EcoAssistContextButton context="tailor-made">{curated.assist.tailor}</EcoAssistContextButton></div></div>}
      <section className="bg-eco-cream py-14 md:py-20">
        <Reveal className="mx-auto max-w-[720px] px-5 md:px-8">
          {done ? (
            <div data-testid="trip-success" className="rounded-2xl border border-eco-ink/10 bg-white p-8 text-center shadow-sm">
              <PartyPopper size={36} className="mx-auto text-eco-gold" />
              <h2 className="mt-4 text-2xl leading-tight font-semibold tracking-[-0.02em] text-eco-ink">{t('wiz.success.t')}</h2>
              <p className="eco-secondary mt-2 text-eco-ink/60">{t('form.success')}</p>
              <p className="mt-6 text-xs font-semibold tracking-[0.14em] uppercase text-eco-ink/45">Request ID</p>
              <p data-testid="trip-reference" className="eco-number mt-1 text-3xl leading-tight font-bold tracking-[-0.02em] text-eco-green">{done.reference}</p>
              <p data-testid="trip-not-confirmed-note" className="mx-auto mt-5 max-w-md rounded-xl bg-eco-gold/10 px-4 py-3 text-xs font-semibold text-eco-ink/70">{t('req.received.note')}</p>
              <Link to="/my-trip" data-testid="trip-success-mytrip" className="eco-btn-primary mt-8">
                {t('nav.mytrip')} <ArrowRight size={15} />
              </Link>
            </div>
          ) : (
            <div className="rounded-2xl border border-eco-ink/10 bg-white p-6 shadow-sm md:p-8">
              <div className="mb-8 flex gap-1.5">
                {STEPS.map((s, i) => (
                  <span key={s} className={`h-1 flex-1 rounded-full transition-colors duration-500 ${i <= step ? 'bg-eco-gold' : 'bg-eco-ink/10'}`} />
                ))}
              </div>
              <p className="mb-6 text-xs font-semibold tracking-[0.14em] uppercase text-eco-gold">
                {String(step + 1).padStart(2, '0')} / {String(STEPS.length).padStart(2, '0')} — {STEPS[step] === 'needs' ? t('wiz.needs') : STEPS[step] === 'style' ? curated.stylesTitle : t(`wiz.${STEPS[step]}`)}
              </p>

              {STEPS[step] === 'destination' && (
                <div data-testid="wiz-step-destination">
                  <div className="flex flex-wrap gap-3">
                    {WIZ_DESTINATIONS.map((d) => (
                      <Chip key={d} testid={`wiz-dest-${d.toLowerCase().replace(/\s+/g, '-')}`} active={data.destinations.includes(d)} onClick={() => toggle('destinations', d)}>
                        {d}
                      </Chip>
                    ))}
                    <Chip testid="wiz-dest-notsure" active={data.destinations.includes('not-sure')} onClick={() => toggle('destinations', 'not-sure')}>
                      {t('wiz.notsure')}
                    </Chip>
                  </div>
                  {data.destinations.filter((d) => WEATHER_KEY_OF_DESTINATION[d]).length > 0 && (
                    <div className="mt-6 flex flex-wrap gap-4">
                      {data.destinations.filter((d) => WEATHER_KEY_OF_DESTINATION[d]).map((d) => (
                        <DestinationWeather key={d} locationKey={WEATHER_KEY_OF_DESTINATION[d]} testid={`wiz-weather-${WEATHER_KEY_OF_DESTINATION[d]}`} />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {STEPS[step] === 'dates' && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <label><span className="eco-field-label">{t('flights.outbound')}</span><input data-testid="wiz-dates" type="date" value={data.departure_date} onChange={(e) => set('departure_date', e.target.value)} className={inputCls} /></label>
                  <label><span className="eco-field-label">{t('flights.return')}</span><input type="date" value={data.return_date} onChange={(e) => set('return_date', e.target.value)} className={inputCls} /></label>
                  <label className="sm:col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={data.flexible_dates} onChange={(e) => set('flexible_dates', e.target.checked)} />{t('journey.flexible')}</label>
                </div>
              )}

              {STEPS[step] === 'travellers' && (
                <div className="flex flex-wrap gap-3" data-testid="wiz-step-travellers">
                  {TRAV_OPTS.map((o) => (
                    <Chip key={o} testid={`wiz-trav-${o}`} active={data.travelers === o} onClick={() => set('travelers', o)}>{t(o)}</Chip>
                  ))}
                </div>
              )}

              {STEPS[step] === 'style' && (
                <div data-testid="wiz-step-style">
                  <p className="mb-4 text-sm text-eco-ink/60">{curated.stylesHelp}</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {JOURNEY_STYLES.map((style) => <Chip key={style} testid={`wiz-style-${style}`} active={data.travelStyle === style} onClick={() => set('travelStyle', style)}>{curated.styles[style]}</Chip>)}
                  </div>
                </div>
              )}

              {STEPS[step] === 'interests' && (
                <div className="flex flex-wrap gap-3" data-testid="wiz-step-interests">
                  {INTERESTS.map((i) => (
                    <Chip key={i.id} testid={`wiz-int-${i.id.toLowerCase()}`} active={data.interests.includes(i.id)} onClick={() => toggle('interests', i.id)}>{t(i.key)}</Chip>
                  ))}
                </div>
              )}

              {STEPS[step] === 'needs' && (
                <div data-testid="wiz-step-needs">
                  <div className="flex flex-wrap gap-3">{NEEDS.map((n) => <Chip key={n.id} testid={`wiz-need-${n.id.toLowerCase().replace(/\s+/g, '-')}`} active={data.needs.includes(n.id)} onClick={() => toggle('needs', n.id)}>{t(n.key)}</Chip>)}</div>
                  {data.needs.includes('Flights') && <div className="mt-6 grid gap-4 rounded-xl bg-eco-cream p-4 sm:grid-cols-2"><input value={data.departure_country} onChange={(e) => set('departure_country', e.target.value)} placeholder={t('flights.departureCountry')} className={inputCls} /><input value={data.departure_airport} onChange={(e) => set('departure_airport', e.target.value)} placeholder={t('flights.departureAirport')} className={inputCls} /><select value={data.cabin_preference} onChange={(e) => set('cabin_preference', e.target.value)} className={inputCls}><option value="">{t('flights.noPreference')}</option><option>Economy</option><option>Premium Economy</option><option>Business</option></select><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={data.inter_island_flight_assistance} onChange={(e) => set('inter_island_flight_assistance', e.target.checked)} />{t('flights.interisland.option')}</label></div>}
                  <p className="mt-6 mb-3 text-sm font-semibold">{t('journey.transport')}</p>
                  <div className="flex flex-wrap gap-3">{TRANSPORT_OPTS.map((item) => <Chip key={item.id} active={data.transport_preferences.includes(item.id)} onClick={() => set('transport_preferences', item.id === 'No transport assistance' ? [item.id] : [...data.transport_preferences.filter((value) => value !== 'No transport assistance' && value !== item.id), ...(data.transport_preferences.includes(item.id) ? [] : [item.id])])}>{t(item.key)}</Chip>)}</div>
                </div>
              )}

              {STEPS[step] === 'accommodation' && (
                <div className="flex flex-wrap gap-3" data-testid="wiz-step-accommodation">
                  {ACCOM_OPTS.map((o) => (
                    <Chip key={o.id} testid={`wiz-acc-${o.key}`} active={data.accommodation === o.id} onClick={() => set('accommodation', o.id)}>{t(o.key)}</Chip>
                  ))}
                  <div className="mt-2">
                    <Link to={`/accommodations${data.destinations.length ? `?destination=${encodeURIComponent(data.destinations[0])}` : ''}`} data-testid="wiz-more-stays" className="inline-flex items-center gap-1.5 text-sm font-semibold text-eco-green hover:text-eco-deep">
                      {t('s22.more.link')} <ArrowRight size={13} />
                    </Link>
                  </div>
                </div>
              )}

              {STEPS[step] === 'notes' && (
                <div className="space-y-4">
                  <div className="grid max-h-64 gap-2 overflow-y-auto pr-1 sm:grid-cols-2" data-testid="wiz-step-experiences">
                    {expPool.map((e) => {
                      const active = data.experiences.includes(e.slug);
                      return (
                        <button
                          key={e.slug}
                          type="button"
                          data-testid={`wiz-exp-${e.slug}`}
                          onClick={() => toggle('experiences', e.slug)}
                          className={`flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-left text-xs font-medium transition-colors ${
                            active ? 'border-eco-green bg-eco-green/10 text-eco-green' : 'border-eco-ink/15 text-eco-ink/65 hover:border-eco-gold'
                          }`}
                        >
                          {active && <Check size={13} />} {e.name}
                        </button>
                      );
                    })}
                  </div>
                  <textarea data-testid="wiz-notes" rows={4} value={data.notes} onChange={(e) => set('notes', e.target.value)} placeholder={t('wiz.notes.ph')} className={inputCls} />
                  <label><span className="eco-field-label">{t('journey.language')}</span><select value={data.preferred_language} onChange={(e) => set('preferred_language', e.target.value)} className={inputCls}><option value="pt">Português</option><option value="en">English</option><option value="fr">Français</option><option value="de">Deutsch</option></select></label>
                </div>
              )}

              {STEPS[step] === 'review' && (
                <div data-testid="wiz-step-review">
                  <h3 className="text-lg leading-[1.3] font-semibold tracking-[-0.015em] text-eco-ink">{t('wiz.yourtrip')}</h3>
                  <dl className="mt-4 space-y-3">
                    {reviewRows.map(([k, v]) => (
                      <div key={k} className="flex gap-3 text-sm">
                        <dt className="w-32 shrink-0 text-eco-ink/45">{k}</dt>
                        <dd className="font-medium text-eco-ink">{v}</dd>
                      </div>
                    ))}
                  </dl>
                  <div className="mt-6 grid gap-4 border-t border-eco-ink/10 pt-6 sm:grid-cols-3">
                    <input data-testid="wiz-name" required value={data.name} onChange={(e) => set('name', e.target.value)} placeholder={t('form.name')} className={inputCls} />
                    <input data-testid="wiz-email" required type="email" value={data.email} onChange={(e) => set('email', e.target.value)} placeholder={t('form.email')} className={inputCls} />
                    <input data-testid="wiz-whatsapp" value={data.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} placeholder={t('wiz.wa.ph')} className={inputCls} />
                    <input data-testid="wiz-country" value={data.country} onChange={(e) => set('country', e.target.value)} placeholder={t('journey.country')} className={inputCls} />
                  </div>
                  <div className="mt-4">
                    <ConsentCheckboxes testid="wiz-consent" value={consents} onChange={setConsents} />
                  </div>
                </div>
              )}

              <div className="mt-8 flex items-center justify-between">
                <button type="button" data-testid="wiz-back" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="eco-btn-text text-eco-ink/50 hover:text-eco-green disabled:opacity-0">
                  <ArrowLeft size={15} /> {t('wiz.back')}
                </button>
                {STEPS[step] === 'review' ? (
                  <button type="button" data-testid="wiz-submit" onClick={submit} disabled={sending || !data.name || !data.email || !consents.terms} className="inline-flex min-h-12 items-center gap-2 rounded-[10px] bg-eco-gold px-6 text-[15px] font-semibold text-eco-deep transition-colors duration-200 hover:bg-[#b8944f] disabled:opacity-50">
                    {sending ? <Loader2 size={16} className="animate-spin" /> : t('wiz.submit.t')}
                  </button>
                ) : (
                  <button type="button" data-testid="wiz-next" onClick={() => canNext() && setStep(step + 1)} disabled={!canNext()} className="eco-btn-primary disabled:opacity-50">
                    {t('wiz.next')} <ArrowRight size={15} />
                  </button>
                )}
              </div>
            </div>
          )}
        </Reveal>
      </section>
    </div>
  );
}
